import { Box, Text } from "@mantine/core";

/**
 * A before → after list, field by field.
 *
 * Lifted out of the audit table so that a maintenance plan's proposed change and the audit
 * entry it eventually produces are rendered by the same component. They describe the same
 * thing at two moments, and the moment an operator is deciding whether to approve something
 * is the worst one to make them re-learn a layout.
 *
 * Both sides are short by construction -- the audit log stores only the fields that differed,
 * and a plan item stores only the fields its parse is entitled to write -- so field by field
 * beats a JSON blob. The reason anyone opens either view is to see what moved.
 */

export type Fields = Record<string, unknown> | null | undefined;

export function FieldDiff({ before, after }: { before: Fields; after: Fields }) {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])];
  if (!keys.length)
    return (
      <Text component="span" fz="inherit" c="dimmed">
        —
      </Text>
    );

  return (
    <Box
      component="ul"
      style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 2 }}
    >
      {keys.map((key) => {
        const from = formatValue(before?.[key]);
        const to = formatValue(after?.[key]);
        return (
          // A field the parse leaves exactly as it found it, shown inside a change it is
          // part of.
          <Box component="li" key={key} c={from === to ? "dimmed" : undefined}>
            <Text component="span" fz="inherit" c="dimmed" mr={8}>
              {key}
            </Text>
            <Text component="span" fz="inherit" c="dimmed" td="line-through">
              {from}
            </Text>
            <span aria-hidden="true"> → </span>
            <Text component="span" fz="inherit" c="accent">
              {to}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * An em dash for absent, the string itself for a string, JSON for anything else.
 *
 * Null renders the same as missing on purpose. In this schema a null parsed field means "the
 * title did not say", which is exactly what an absent one means too, and drawing a
 * distinction the data does not carry would invite somebody to act on it.
 */
export function formatValue(value: unknown): string {
  if (value === undefined || value === null) return "—";
  return typeof value === "string" ? value : JSON.stringify(value);
}
